import { ListItem } from "../models/ListItem";
import {
  PagedResultDTO,
  PaginatedRequest,
} from "../models/pagedResultDto.model";
import { httpService } from "./httpService";

class GenericService<T, TListItem extends ListItem = ListItem> {
  private baseApiPath: string;

  constructor(baseApiPath: string) {
    this.baseApiPath = baseApiPath;
  }

  async getAll(normalize = true, toast = false): Promise<T[]> {
    return await httpService.get<T[]>(`${this.baseApiPath}/GetAll`, undefined, {
      unwrap: true,
      normalize,
      toast,
    });
  }

  async getListItems(normalize = true, toast = false): Promise<TListItem[]> {
    return await httpService.get<TListItem[]>(
      `${this.baseApiPath}/GetListItems`,
      undefined,
      {
        unwrap: true,
        normalize,
        toast,
      }
    );
  }

  async getCitiesByCountryId(
    normalize = true,
    toast = false
  ): Promise<TListItem[]> {
    const countryId = 101;
    return await httpService.get<TListItem[]>(
      `${this.baseApiPath}/GetCitiesByCountryId/${countryId}`,
      undefined,
      {
        unwrap: true,
        normalize,
        toast,
      }
    );
  }

  async getById(
    id: number | string,
    normalize = false,
    toast = false
  ): Promise<T | null> {
    try {
      return await httpService.get<T>(
        `${this.baseApiPath}/GetById/${id}`,
        undefined,
        {
          unwrap: true,
          normalize,
          toast,
        }
      );
    } catch (error) {
      console.error(
        `Failed to fetch item with ID ${id} from ${this.baseApiPath}`,
        error
      );
      return null;
    }
  }

  async getAllPaginated<T>(
    request: PaginatedRequest
  ): Promise<PagedResultDTO<T> | null> {
    const {
      pageNumber,
      pageSize,
      isActive,
      sortField,
      sortOrder,
      globalFilter = "",
    } = request;

    try {
      const encodedFilter = encodeURIComponent(globalFilter);
      const url = `${this.baseApiPath}/getAllPaginated?pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}&globalFilter=${encodedFilter}`;

      return await httpService.get<PagedResultDTO<T>>(url, undefined, {
        unwrap: true,
        normalize: true,
        toast: false,
      });
    } catch (error) {
      console.error("Error fetching paginated data:", error);
      return null;
    }
  }
  async create(item: Partial<T>, toast = true): Promise<T> {
    const result = await httpService.post<T>(
      `${this.baseApiPath}/Create`,
      item,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast,
      }
    );
    return result as T; // assert here to help TypeScript
  }

  async update(
    id: number | string,
    item: Partial<T>,
    toast = true
  ): Promise<T> {
    const result = await httpService.put<T>(
      `${this.baseApiPath}/Update/${id}`,
      item,
      undefined,
      {
        unwrap: true,
        normalize: false,
        toast,
      }
    );
    return result as T;
  }

  async delete(id: number | string, toast = true): Promise<boolean> {
    try {
      await httpService.delete(`${this.baseApiPath}/Delete/${id}`, undefined, {
        unwrap: true,
        normalize: false,
        toast,
      });
      return true;
    } catch (error) {
      console.error(
        `Failed to delete item with ID ${id} from ${this.baseApiPath}`,
        error
      );
      return false;
    }
  }
  async updateStatus(id: number | string, toast = true): Promise<boolean> {
    try {
      await httpService.delete(
        `${this.baseApiPath}/UpdateStatus/${id}`,
        undefined,
        {
          unwrap: true,
          normalize: false,
          toast,
        }
      );
      return true;
    } catch (error) {
      console.error(
        `Failed to delete item with ID ${id} from ${this.baseApiPath}`,
        error
      );
      return false;
    }
  }
}

export default GenericService;
